// @ts-strict-ignore
import { Directive, Input, NgZone, QueryList, ViewChildren } from '@angular/core';
import { Observable, Observer, of } from 'rxjs';
import { LMThemeService } from '@banner/lm-theme.service';
import { LandmarkPanel, LMDialogInfo, LMNavigationService, LMRefreshRequest } from '@app/components';
import { LMErrorUtilService, LMOverlayService, LMUrlUtilService } from '@app/services';
import { AppConstants } from '@app/utils';

@Directive()
export abstract class LMTwelveColumnPanelBaseComponent {

  @Input() set fromDataView(dataView: LMDataView) {}
  @Input() panel: LandmarkPanel;
  @Input() isFlyoutContent = false;

  @ViewChildren (LMTwelveColumnPanelBaseComponent) lmTwelveColumnPanelComponentList: QueryList<number>;

  public cardClasses = '';

  constructor(
    protected lmThemeService: LMThemeService,
    protected lmOverlayService: LMOverlayService,
    protected lmUrlUtilService: LMUrlUtilService,
    protected lmNavigationService: LMNavigationService,
    protected lmErrorUtilService: LMErrorUtilService,
    protected zone: NgZone
  ) {}

  isModified(): boolean {
    return false;
  }

  // eslint-disable-next-line no-unused-vars
  public refresh(request?: LMRefreshRequest): Observable<boolean> {
    return of(true);
  }

  save(callBack: Observer<boolean>) {
    callBack.next(true);
    callBack.complete();
  }

  abstract savePanelState(): void;
  abstract clearPanelState(): void;

  protected _setContentClasses(panel: LandmarkPanel) {
    this.cardClasses = 'card columns ';

    if (panel.type === 'MenuLinkBoxPanel') {
      this.cardClasses += ' no-shadow border-less ';
    }

    switch(panel.spanningColumns) {
      case 1:
        this.cardClasses += 'one';
        break;
      case 2:
        this.cardClasses += 'two';
        break;
      case 3:
        this.cardClasses += 'three';
        break;
      case 4:
        this.cardClasses += 'four';
        break;
      case 5:
        this.cardClasses += 'five';
        break;
      case 6:
        this.cardClasses += 'six';
        break;
      case 7:
        this.cardClasses += 'seven';
        break;
      case 8:
        this.cardClasses += 'eight';
        break;
      case 9:
        this.cardClasses += 'nine';
        break;
      case 10:
        this.cardClasses += 'ten';
        break;
      case 11:
        this.cardClasses += 'eleven';
        break;
      case 12:
      default:
        this.cardClasses += 'twelve';
        break;
    }
  }

  protected openNavigationInOverlay(url: string, navigationName: string, overlayNavType: 'list' | 'form' | 'page', event: any): void {
    if (this.lmOverlayService.isOpening) {
      return;
    }
    this.lmOverlayService.isOpening = true;

    const info: LMDialogInfo = { message: Soho.Locale.translate('UnsavedChangesContinuingMsg')  };
    // Need to make sure the parent runs the unsaved changes, for the case that a parent list is dirty and
    // a navigation is clicked on the child
    this.lmOverlayService.routerOutletUnsavedChanges(info).subscribe((response) => {
      if (response){
        // need in a timeout for the case where it's a list on a page, unsaved changes is seen and
        // the user clicked to save. This causes the page to reload, so we need to be in the next tick
        // to not have an error
        this.zone.runOutsideAngular(() => {
          setTimeout(() => this._openNavigationInOverlay(url, navigationName, overlayNavType, event), 1);
        });
      } else {
        this.lmOverlayService.isOpening = false;
      }
    });
  }

  private _openNavigationInOverlay(url: string, navigationName: string, overlayNavType: 'list' | 'form' | 'page', event: LMFormOverlayClickedEvent) {
    const navigationUrl = this.lmUrlUtilService.updateUrlParams(url, { output: 'spec' });
    this.lmNavigationService.getActionNavigationUrl(navigationUrl).subscribe(response => {
      if (response.messageId && !response.redirectUrl) {
        // Common Inbox Integration - Direct route to open notification may return error messages.
        this.lmErrorUtilService.handleError(Soho.Locale.translate(response.messageId));
        this.lmOverlayService.isOpening = false;
        return;
      } else if (response.status && response.status === AppConstants.STATUS_SYSTEM_ERROR) {
        this.lmErrorUtilService.handleError(response.message, response.details, response.status, response.statusText);
        this.lmOverlayService.isOpening = false;
        return;
      }

      const redirectUrl = this.lmUrlUtilService.toRelativeUrl(response.redirectUrl, true, true);

      let relativeUrl = this.lmUrlUtilService.toRelativeUrl(redirectUrl);
      relativeUrl = this.lmUrlUtilService.encodePlus(relativeUrl);
      relativeUrl = this.lmUrlUtilService.encodeParens(relativeUrl);

      const isGenUrl = this.lmUrlUtilService.isGenUrlUsingHref(relativeUrl); // urlParams['csk.gen'] === 'true';
      const urlPath = this.lmUrlUtilService.getUrlPath(relativeUrl);
      const newResource = this.lmUrlUtilService.getResource(urlPath, isGenUrl);
      const newResourceId = newResource.generateBusObjectId(false);

      // worry about optimization for opening the same nav on a different record in the future
      this.lmOverlayService.open({
        open: true,
        url: relativeUrl,
        listOpenedId: this.panel.id,
        resourceId: newResourceId,
        isNavigation: true,
        navigationName: navigationName,
        replaceCurrentContent: this.lmOverlayService.hasOpenOverlay,
        navigationType: overlayNavType,
        insideOverlayNav: this.isFlyoutContent,
        event: event
      });
    });
  }
}
